import React, { Component, Suspense, lazy, useEffect, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyResponseType } from '@services';
import { cepMask } from '@/shared/utils/input-masks';
import { formatStreetWithComplement } from '@/shared/utils/property-detail-fields';
import {
  collectCondominiumUnitRows,
  formatDistanceToSea,
} from '@/shared/utils/property-attribute-rows';
import { Text, UnstyledButton } from '@/shared/ui';
import { SectionCard, InfoRow } from '../SectionCard';
import { PropertyDialog } from '../PropertyDialog';

const PropertyMap = lazy(() => import('../PropertyMap'));

interface MapBoundaryState {
  hasError: boolean;
}

/**
 * Catches dynamic-import or render failures in the lazy-loaded map,
 * degrading to a placeholder instead of crashing the whole page.
 */
class MapErrorBoundary extends Component<{ children: ReactNode; fallbackLabel: string }, MapBoundaryState> {
  state: MapBoundaryState = { hasError: false };

  static getDerivedStateFromError(): MapBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('MapErrorBoundary caught an error', error, info);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full bg-[var(--surface-tertiary)] flex items-center justify-center">
          <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
            {this.props.fallbackLabel}
          </Text>
        </div>
      );
    }
    return this.props.children;
  }
}

interface LocationCardProps {
  property: PropertyResponseType;
}

interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Formats the street line from property number and street name.
 *
 * @param property - Property data source
 * @returns Formatted street string or undefined when no street exists
 */
function formatStreetLine(property: PropertyResponseType): string | undefined {
  const loc = property.location;
  if (!loc?.street) return undefined;
  return loc.number ? `${loc.number} ${loc.street}` : loc.street;
}

/**
 * Detects placeholder street names that cannot be geocoded.
 *
 * @param street - Raw street string from property data
 * @returns True when the street is a known placeholder pattern
 */
function isPlaceholderStreet(street: string | undefined): boolean {
  if (!street) return true;
  const normalized = street.trim().toLowerCase();
  const placeholders = ['não informada', 'nao informada', 'não informado', 'n/a', 's/n', '-'];
  return placeholders.some((p) => normalized === p || normalized === `rua ${p}`);
}

/**
 * Builds Nominatim query candidates ordered from most to least specific.
 * Skips street when it is a placeholder (e.g. "Rua não informada").
 *
 * @param property - Property whose address is geocoded
 * @returns Array of query strings to try in order, empty when too few parts
 */
function buildGeocodeQueries(property: PropertyResponseType): string[] {
  const full = _buildAddressParts(property);
  if (full.length < 2) return [];
  const queries: string[] = [];
  _appendPostalVariant(queries, full, property.location);
  _appendFallbackVariants(queries, property);
  return queries;
}

/**
 * Assembles the main address parts (street, neighborhood, city, state).
 */
function _buildAddressParts(property: PropertyResponseType): string[] {
  const loc = property.location;
  const hasRealStreet = !isPlaceholderStreet(loc?.street);
  const street = hasRealStreet ? formatStreetLine(property) : undefined;
  return [street, loc?.neighborhood, loc?.city, loc?.state].filter(Boolean) as string[];
}

/**
 * Appends the primary query optionally suffixed with postal code.
 */
function _appendPostalVariant(queries: string[], full: string[], loc: any): void {
  const fullQuery = full.join(', ');
  const variant = loc?.postal_code ? `${fullQuery}, ${cepMask(loc.postal_code).masked}` : fullQuery;
  queries.push(variant);
}

/**
 * Adds fallback geocode queries excluding neighborhood or street.
 */
function _appendFallbackVariants(queries: string[], property: PropertyResponseType): void {
  const loc = property.location;
  const hasRealStreet = !isPlaceholderStreet(loc?.street);
  const street = hasRealStreet ? formatStreetLine(property) : undefined;
  const noNeighborhood = [street, loc?.city, loc?.state].filter(Boolean);
  if (noNeighborhood.length >= 2) queries.push(noNeighborhood.join(', '));
  const neighborhoodFallback = [loc?.neighborhood, loc?.city, loc?.state].filter(Boolean);
  if (neighborhoodFallback.length >= 2 && !queries.includes(neighborhoodFallback.join(', '))) {
    queries.push(neighborhoodFallback.join(', '));
  }
}

/**
 * Resolves address coordinates via the OpenStreetMap Nominatim service.
 *
 * @param query - Prebuilt Nominatim query string
 * @returns Coordinates for the first match, or null on failure/no match
 */
async function fetchCoordinates(query: string): Promise<Coordinates | null> {
  try {
    const params = new URLSearchParams({ q: query, format: 'json', limit: '1', countrycodes: 'br' });
    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
    const results = await response.json();
    if (!Array.isArray(results) || results.length === 0) return null;
    return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
  } catch {
    return null;
  }
}

/**
 * Geocodes a property's address into map coordinates.
 * Tries progressively simpler queries until one returns a result.
 *
 * @param property - Property to locate on the map
 * @returns Coordinates, or null when the address cannot be resolved
 */
async function geocodeAddress(property: PropertyResponseType): Promise<Coordinates | null> {
  const queries = buildGeocodeQueries(property);
  for (const query of queries) {
    const result = await fetchCoordinates(query);
    if (result) return result;
  }
  return null;
}

/**
 * Renders the property location section with a lazily-loaded map,
 * falling back to backend coordinates or on-demand geocoding.
 *
 * @param property - Property whose location is displayed
 */
/**
 * The distance to the sea and the "Condomínio e unidade" rows: condominium,
 * building, block and unit, quadra and lot, development and developer.
 *
 * @param props - The property.
 * @returns The extra rows, nothing when none is informed.
 */
const LocationAttributeRows: React.FC<LocationCardProps> = ({ property }) => {
  const { t } = useTranslation('features/properties');
  const distance = formatDistanceToSea(property.location);
  const towerName = property.category_specifics?.tower_name;
  const unitRows = collectCondominiumUnitRows(
    property.location,
    property.condominium,
    typeof towerName === 'string' ? towerName : undefined,
    t,
  );
  return (
    <>
      {distance && <InfoRow label={t('detailView.location.distanceToSea')} value={distance} />}
      {unitRows.length > 0 && (
        <div data-testid="property-location-unit">
          <Text
            variant="meta"
            component="p"
            className="m-0 mt-[var(--space-2-5)] mb-[var(--space-0-5)] [font-weight:var(--font-bold)] text-[var(--text-tertiary)]"
          >
            {t('detailView.location.unitHeading')}
          </Text>
          {unitRows.map((row) => <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />)}
        </div>
      )}
    </>
  );
};

export const LocationCard: React.FC<LocationCardProps> = ({ property }) => {
  const { t } = useTranslation('features/properties');
  const loc = property.location;
  const backendLat = loc?.latitude;
  const backendLng = loc?.longitude;
  const hasBackendCoords = backendLat != null && backendLng != null && backendLat !== 0 && backendLng !== 0;

  const [coords, setCoords] = useState<Coordinates | null>(
    hasBackendCoords ? { lat: backendLat!, lng: backendLng! } : null,
  );
  const [geocoding, setGeocoding] = useState(!hasBackendCoords);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (hasBackendCoords) {
      setCoords({ lat: backendLat!, lng: backendLng! });
      setGeocoding(false);
      return;
    }

    let cancelled = false;
    setGeocoding(true);

    geocodeAddress(property).then((result) => {
      if (!cancelled) {
        setCoords(result);
        setGeocoding(false);
      }
    });

    return () => { cancelled = true; };
  }, [loc?.street, loc?.number, loc?.city, loc?.state, loc?.postal_code, loc?.neighborhood, hasBackendCoords, backendLat, backendLng]);

  const showMap = coords !== null;
  const streetLine = formatStreetWithComplement(loc);

  return (
    <SectionCard title={t('detailView.location.title')}>
      {geocoding ? (
        <div className="rounded-(--radius-md) bg-[var(--surface-tertiary)] h-[140px] flex items-center justify-center mb-[var(--space-2)] animate-pulse">
          <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
            {t('detailView.location.locating')}
          </Text>
        </div>
      ) : showMap ? (
        // The preview is a 140px strip -- enough to say "this is the right
        // part of town", not enough to read a street off. Opening it is what
        // the broker actually wants when they look at it.
        <UnstyledButton
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={t('detailView.location.viewFullscreen')}
          data-testid="property-map-expand"
          className="mb-[var(--space-2)] block h-[140px] w-full overflow-hidden rounded-(--radius-md)"
        >
          <MapErrorBoundary fallbackLabel={t('detailView.location.mapUnavailable')}>
            <Suspense
              fallback={
                <div className="w-full h-full bg-[var(--surface-tertiary)] animate-pulse flex items-center justify-center">
                  <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">{t('detailView.location.loadingMap')}</Text>
                </div>
              }
            >
              <PropertyMap latitude={coords!.lat} longitude={coords!.lng} />
            </Suspense>
          </MapErrorBoundary>
        </UnstyledButton>
      ) : (
        <div className="rounded-(--radius-md) bg-[var(--surface-tertiary)] h-[140px] flex items-center justify-center mb-[var(--space-2)]">
          <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
            {t('detailView.location.addressNotFound')}
          </Text>
        </div>
      )}

      {streetLine && <InfoRow label={t('detailView.location.street')} value={streetLine} />}
      {loc?.city && (
        <InfoRow label={t('detailView.location.city')} value={`${loc.city}${loc.state ? `/${loc.state}` : ''}`} />
      )}
      {loc?.neighborhood && <InfoRow label={t('detailView.location.neighborhood')} value={loc.neighborhood} />}
      {loc?.postal_code && <InfoRow label="CEP" value={cepMask(loc.postal_code).masked} />}
      <LocationAttributeRows property={property} />

      {expanded && coords && (
        <PropertyDialog
          title={streetLine || t('detailView.location.title')}
          subtitle={[loc?.neighborhood, loc?.city && `${loc.city}${loc.state ? `/${loc.state}` : ''}`]
            .filter(Boolean)
            .join(', ')}
          width="680px"
          onClose={() => setExpanded(false)}
          data-testid="property-map-dialog"
        >
          <div className="h-[420px] w-full overflow-hidden rounded-(--radius-md)">
            <MapErrorBoundary fallbackLabel={t('detailView.location.mapUnavailable')}>
              <Suspense
                fallback={
                  <div className="flex h-full w-full animate-pulse items-center justify-center bg-[var(--surface-tertiary)]">
                    <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
                      {t('detailView.location.loadingMap')}
                    </Text>
                  </div>
                }
              >
                <PropertyMap latitude={coords.lat} longitude={coords.lng} />
              </Suspense>
            </MapErrorBoundary>
          </div>
        </PropertyDialog>
      )}
    </SectionCard>
  );
};
